<template>
    <fieldset class="fieldset">
        <legend>{{ trans('Time') }}</legend>

        <div class="row">
            <div class="col-12 col-md-6 form-group">
                <label class="form-control-label" for="dateStart">
                    {{ trans('Start date') }}
                    <span class="required">*</span>
                </label>

                <vue-flatpickr
                    v-model="dateStart"
                    :config="dateConfig"
                    name="dateStart"
                    class="form-control"
                    :class="{ 'is-invalid': errors && errors['timings.dateStart'] }"
                    autocomplete="off"
                    @update:modelValue="doCalculations(true)"
                >
                </vue-flatpickr>
                <div class="invalid-feedback d-block" v-if="errors && errors['timings.dateStart']">
                    {{ errors['timings.dateStart'][0] }}
                </div>
            </div>

            <div class="col-12 col-md-6 form-group">
                <label class="form-control-label" for="date_end">
                    {{ trans('End date') }}
                    <span class="required">*</span>
                </label>

                <vue-flatpickr
                    v-model="dateEnd"
                    :config="{
                        allowInput: true,
                        dateFormat: 'Y-m-d',
                        altInput: true,
                        altFormat: 'd/m/Y',
                        minDate: this.modelValue.dateStart,
                    }"
                    :class="{ 'is-invalid': errors && errors['timings.dateEnd'] }"
                    name="dateEnd"
                    class="form-control"
                    autocomplete="off"
                    @update:modelValue="doCalculations()"
                >
                </vue-flatpickr>

                <div class="invalid-feedback d-block" v-if="errors && errors['timings.dateEnd']">
                    {{ errors['timings.dateEnd'][0] }}
                </div>
            </div>

            <div v-if="!isSpecificTime" class="form-group col-12 col-sm-6">
                <label class="form-control-label" for="timeDay">
                    {{ trans('Total per day') }}
                    <span class="required">*</span>
                </label>

                <vue-flatpickr
                    v-model="timeDay"
                    :config="timeConfig"
                    name="timeDay"
                    class="form-control form-control--white-background"
                    autocomplete="off"
                    @update:modelValue="doCalculations(false, true)"
                    :class="{ 'is-invalid': errors && timeDay == '00:00' }"
                >
                </vue-flatpickr>
                <div class="invalid-feedback d-block" v-if="errors && timeDay == '00:00'">
                    {{ trans('Total working time can not be zero or negative') }}
                </div>
            </div>

            <div v-if="!isSpecificTime" class="form-group col-12 col-sm-6">
                <label class="form-control-label" for="timeTotal">
                    {{ trans('Total time') }}
                    <span class="required">*</span>
                </label>

                <input
                    name="timeTotal"
                    id="timeTotal"
                    class="form-control"
                    :value="timeTotal"
                    :disabled="true"
                    required
                    type="text"
                />
            </div>

            <div v-if="isSpecificTime" class="form-group col-12 col-sm-6">
                <label class="form-control-label" for="timeStart">
                    {{ trans('Start time') }}
                    <span class="required">*</span>
                </label>

                <vue-flatpickr
                    v-model="timeStart"
                    :config="timeConfig"
                    name="timeStart"
                    class="form-control form-control--white-background"
                    :class="{ 'is-invalid': errors && errors['timings.timeStart'] }"
                    autocomplete="off"
                    @update:modelValue="doCalculations(true)"
                >
                </vue-flatpickr>

                <div class="invalid-feedback d-block" v-if="errors && errors['timings.timeStart']">
                    {{ errors['timings.timeStart'][0] }}
                </div>
            </div>

            <div v-if="isSpecificTime" class="form-group col-12 col-sm-6">
                <label class="form-control-label" for="timeEnd">
                    {{ trans('End time') }}
                    <span class="required">*</span>
                </label>

                <vue-flatpickr
                    v-model="timeEnd"
                    :config="timeConfig"
                    :class="{ 'is-invalid': errors && errors['timings.timeEnd'] }"
                    name="timeEnd"
                    class="form-control form-control--white-background"
                    autocomplete="off"
                    @update:modelValue="doCalculations()"
                >
                </vue-flatpickr>

                <div class="invalid-feedback d-block" v-if="errors && errors['timings.timeEnd']">
                    {{ errors['timings.timeEnd'][0] }}
                </div>
            </div>

            <div class="col-12">
                <div class="row">
                    <div class="form-group col-6">
                        <div class="switch-form ml-0 d-flex align-items-center">
                            <label class="switch mb-0 mr-2">
                                <input
                                    type="checkbox"
                                    :true-value="1"
                                    :false-value="0"
                                    name="active"
                                    v-model="isSpecificTime"
                                    @change="emitValues"
                                />
                                <span class="slider round"></span>
                            </label>
                            <label class="mb-0">{{ trans('Specific time') }}</label>
                        </div>
                    </div>

                    <div class="form-group col-6">
                        <div class="switch-form ml-0 d-flex align-items-center">
                            <label class="switch mb-0 mr-2">
                                <input
                                    type="checkbox"
                                    value="1"
                                    name="active"
                                    v-model="isRepeatable"
                                    @change="emitValues"
                                />
                                <span class="slider round"></span>
                            </label>
                            <label class="mb-0">{{ trans('Repeatable') }}</label>
                        </div>
                    </div>

                    <div v-if="!fromSlot" class="form-group col-6">
                        <div class="switch-form ml-0 d-flex align-items-center">
                            <label class="switch mb-0 mr-2">
                                <input
                                    type="checkbox"
                                    v-model="linkedDates"
                                    @change="emitValues"
                                />
                                <span class="slider round"></span>
                            </label>
                            <label class="mb-0">{{ trans('Link dates') }}</label>
                        </div>
                    </div>
                </div>
            </div>

            <repeatable-component
                v-show="isRepeatable"
                :modelValue="repeatable"
                @update:modelValue="repeatable = $event"
                :date-start="dateStart"
                :errors="errors"
            ></repeatable-component>
        </div>
    </fieldset>
</template>

<script>
import RepeatableComponent from '../../repeatable/RepeatableComponent';
import VueFlatpickr from 'vue-flatpickr-component';
import { ChronoUnit, LocalDate } from '@js-joda/core';
import { timeToMinutes } from '../../../utils/timeToMinutes';
import { minutesToTime } from '../../../utils/minutesToTime';

export default {
    components: {
        RepeatableComponent,
        VueFlatpickr,
    },
    props: {
        modelValue: {
            required: true,
            type: Object,
        },
        errors: {
            required: false,
            type: Object,
        },
        fromSlot: {
            type: Boolean,
            default: false,
        },
    },
    data: function () {
        return {
            // Timings
            dateStart: this.modelValue.dateStart,
            dateEnd: this.modelValue.dateEnd,
            isSpecificTime: this.modelValue.isSpecificTime,
            timeStart: this.modelValue.timeStart,
            timeEnd: this.modelValue.timeEnd,
            timeDay: '08:00',
            timeTotal: '08:00',
            linkedDates: this.modelValue.linkedDates !== undefined ? this.modelValue.linkedDates : true,

            // Repeat timings
            isRepeatable: this.modelValue.isRepeatable,
            repeatable: {
                repeatRangeType: this.modelValue.repeatRangeType,
                repeatUntil: this.modelValue.repeatUntil,
                repeatAfter: this.modelValue.repeatAfter,
                repeatFrequency: this.modelValue.repeatFrequency,
                repeatEvery: this.modelValue.repeatEvery,
                repeatEveryType: this.modelValue.repeatEveryType,
                repeatOn: this.modelValue.repeatOn,
                repeatOnEveryMonth: this.modelValue.repeatOnEveryMonth,
            },

            // Configs
            dateConfig: {
                allowInput: true,
                dateFormat: 'Y-m-d',
                altInput: true,
                altFormat: 'd/m/Y',
            },
            timeConfig: {
                enableTime: true,
                noCalendar: true,
                dateFormat: 'H:i',
                time_24hr: true,
            },
        };
    },
    methods: {
        doCalculations(startChanged = true, timeDayChanged = false) {
            let dateStart = this.dateStart;
            let dateEnd = this.dateEnd;

            // Make sure start date isn't after end
            if (dateStart > dateEnd) {
                if (startChanged) {
                    this.dateEnd = dateEnd = dateStart;
                } else {
                    this.dateStart = dateStart = dateEnd;
                }
            }

            let timeStart = this.timeStart;
            let timeEnd = this.timeEnd;
            let timeDay = this.timeDay;

            if (timeDayChanged) {
                let minutesPerDay = timeToMinutes(timeDay);
                let timeStartMinutes = timeToMinutes(timeStart);
                let timeEndMinutes = timeStartMinutes + minutesPerDay;
                const maxMinutesInDay = 24 * 60;
                if (timeEndMinutes >= maxMinutesInDay) {
                    let minutesOver = timeEndMinutes - maxMinutesInDay;
                    timeEndMinutes = timeEndMinutes - minutesOver - 1;
                }
                timeEnd = minutesToTime(timeEndMinutes);
            }

            // Make sure start time isn't after end
            if (timeStart > timeEnd) {
                if (startChanged) {
                    timeEnd = timeStart;
                } else {
                    timeStart = timeEnd;
                }
            }

            const timeStartMinutes = timeToMinutes(timeStart);
            const timeEndMinutes = timeToMinutes(timeEnd);
            const minutesPerDay = timeEndMinutes - timeStartMinutes;
            timeDay = minutesToTime(minutesPerDay);

            dateStart = LocalDate.parse(dateStart);
            dateEnd = LocalDate.parse(dateEnd);

            let amoutOfDaysInPeriod = 1 + dateStart.until(dateEnd, ChronoUnit.DAYS);
            const totalTime = minutesToTime(amoutOfDaysInPeriod * minutesPerDay);

            this.timeStart = timeStart;
            this.timeEnd = timeEnd;
            this.timeDay = timeDay;
            this.timeTotal = totalTime;

            this.emitValues();
        },
        emitValues() {
            this.$emit('update:modelValue', {
                dateStart: this.dateStart,
                dateEnd: this.dateEnd,
                timeStart: this.timeStart,
                timeEnd: this.timeEnd,
                isSpecificTime: this.isSpecificTime,
                linkedDates: this.linkedDates,

                // Repeat
                isRepeatable: this.isRepeatable,
                repeatRangeType: this.repeatable.repeatRangeType,
                repeatUntil: this.repeatable.repeatUntil,
                repeatAfter: this.repeatable.repeatAfter,
                repeatFrequency: this.repeatable.repeatFrequency,
                repeatEvery: this.repeatable.repeatEvery,
                repeatEveryType: this.repeatable.repeatEveryType,
                repeatOn: this.repeatable.repeatOn,
                repeatOnEveryMonth: this.repeatable.repeatOnEveryMonth,
            });
        },
    },
    created() {
        this.doCalculations();
    },
    watch:{
        linkedDates() {
            this.emitValues();
        },
        repeatable: {
            handler: function () {
                this.emitValues();
            },
            deep: true,
        },
    }
};
</script>

<style scoped></style>
